"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Sun,
  Moon,
  ArrowRight,
  Github,
  Link as LinkIcon,
  SlidersHorizontal,
  FileText,
  Calendar as CalendarIcon,
  Clock,
  Tag,
  Search,
} from "lucide-react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { useTheme } from "./src/ThemeContext.jsx";

/**
 * Blog — Matches Projects page UI/UX
 * - Same navbar, theme system, 3D tilt, magnetic buttons, parallax accents
 * - Unified buttons (indigo→cyan gradient) like the rest of the site
 */
export default function BlogPage() {
  const { darkMode, toggleTheme } = useTheme();
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState(null);

  const filtered = POSTS.filter((p) => {
    const matchesQuery = !query || (p.title + p.excerpt + p.tags.join(" ")).toLowerCase().includes(query.toLowerCase());
    const matchesTag = !activeTag || p.tags.includes(activeTag);
    return matchesQuery && matchesTag;
  });

  return (
    <div
      className={
        darkMode
          ? "min-h-screen bg-[#0a0c10] text-white"
          : "min-h-screen bg-white text-gray-900"
      }
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] px-3 py-2 rounded-md bg-indigo-600 text-white"
      >
        Skip to content
      </a>

      {/* NAV — identical to homepage / Projects */}
      <header className="sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto mt-4 px-4 sm:px-6">
          <div
            className={`backdrop-blur rounded-2xl shadow-sm flex items-center justify-between px-4 sm:px-6 py-3 ${
              darkMode
                ? "bg-white/5 border border-white/10"
                : "bg-white/80 border border-white/90"
            }`}
          >
            <a href="/" className="flex items-center gap-3">
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center font-semibold text-white ${
                  darkMode
                    ? "bg-gradient-to-br from-indigo-500 to-cyan-400"
                    : "bg-gradient-to-br from-indigo-600 to-cyan-500 drop-shadow"
                }`}
              >
                ST
              </div>
              <div className="leading-tight">
                <div
                  className={`text-sm font-semibold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Sikandar Tareen
                </div>
                <div
                  className={`text-[11px] ${
                    darkMode ? "text-white/60" : "text-gray-600"
                  }`}
                >
                  Agentic AI Engineer
                </div>
              </div>
            </a>
            <div
              className={`hidden md:flex items-center gap-6 text-sm ${
                darkMode ? "text-white/70" : "text-gray-700"
              }`}
            >
              <a
                href="/"
                className={`${darkMode ? "hover:text-white" : "hover:text-black"}`}
              >
                Home
              </a>
              <a
                href="/projects"
                className={`${darkMode ? "hover:text-white" : "hover:text-black"}`}
              >
                Projects
              </a>
              <a
                href="/blog"
                className={`${darkMode ? "text-white" : "text-black"} font-medium`}
              >
                Blog
              </a>
              <a
                href="/about-me"
                className={`${darkMode ? "hover:text-white" : "hover:text-black"}`}
              >
                About
              </a>

              <ButtonPrimary darkMode={darkMode} href="/contact" small>
                <Sparkles size={14} /> Contact Me
              </ButtonPrimary>
            </div>
          </div>
        </nav>
      </header>

      {/* Parallax accents */}
      <ParallaxField darkMode={darkMode} />

      <main
        id="main"
        className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 pt-10 [perspective:1200px]"
      >
        {/* Hero */}
        <section className="relative overflow-visible">
          <div
            className={`absolute -top-24 -left-24 w-[42rem] h-[42rem] rounded-full blur-3xl ${
              darkMode ? "bg-indigo-600/20" : "bg-indigo-200/60"
            }`}
          />
          <div
            className={`absolute -bottom-32 -right-16 w-[36rem] h-[36rem] rounded-full blur-3xl ${
              darkMode ? "bg-cyan-500/20" : "bg-cyan-200/60"
            }`}
          />

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10"
          >
            <h1 className="text-4xl sm:text-5xl font-extrabold">
              Notes on Agentic AI, RAG, and Eval
            </h1>
            <p className={`${darkMode ? "text-white/80" : "text-gray-700"} mt-3 max-w-2xl`}>
              Practical patterns for multi‑agent systems, retrieval that holds up in prod, and governance that keeps stakeholders calm.
            </p>
          </motion.div>
        </section>

        {/* Filters */}
        <section className="mt-10">
          <div
            className={`rounded-2xl p-4 border ${
              darkMode ? "bg-white/5 border-white/10" : "bg-white border-gray-200"
            }`}
          >
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className={`${darkMode ? "text-white/70" : "text-gray-600"}`}>
                Filter:
              </span>
              {["All", ...TAGS].map(
                (t, i) => (
                  <Chip 
                    key={t} 
                    darkMode={darkMode} 
                    active={i === 0 && !activeTag || activeTag === t}
                    onClick={() => setActiveTag(i === 0 ? null : t)}
                  >
                    {t}
                  </Chip>
                )
              )}
              <div className="ml-auto flex items-center gap-2">
                <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 flex-1 ${
                  darkMode ? "bg-white/5 border-white/10" : "bg-white border-gray-200"
                }`}>
                  <Search size={16} className={darkMode ? "text-white/70" : "text-gray-600"} />
                  <input 
                    value={query} 
                    onChange={(e) => setQuery(e.target.value)} 
                    placeholder="Search posts…" 
                    className={`bg-transparent outline-none w-full ${
                      darkMode ? "placeholder-white/50" : "placeholder-gray-500"
                    }`} 
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section id="grid" className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <PostCard key={p.slug} p={p} darkMode={darkMode} />
          ))}
        </section>

        {/* CTA Banner */}
        <section className="mt-16">
          <TiltCard
            darkMode={darkMode}
            glow
            className={`${
              darkMode
                ? "from-indigo-600/30 to-cyan-500/30 border-white/10 text-white"
                : "from-indigo-100 to-cyan-100 border-gray-200 text-gray-900"
            } rounded-3xl p-8 bg-gradient-to-r border`}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 justify-between">
              <div>
                <h3 className="text-xl font-bold">Want to discuss these patterns?</h3>
                <p
                  className={`${darkMode ? "text-white/80" : "text-gray-700"} text-sm mt-1`}
                >
                  I can share deeper insights and discuss implementation details for your specific use case.
                </p>
              </div>
              <Magnetic>
                <ButtonPrimary darkMode={darkMode} href="/contact">
                  Let's chat about your project <ArrowRight size={16} />
                </ButtonPrimary>
              </Magnetic>
            </div>
          </TiltCard>
        </section>
      </main>

      {/* Footer */}
      <footer className={`py-10 text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'} border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">© {new Date().getFullYear()} Sikandar Tareen</div>
      </footer>

      {/* Theme toggle */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={toggleTheme}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg border transition focus:outline-none focus:ring-2 ${darkMode ? 'border-white/20 bg-[#1a1c20] text-white hover:opacity-90 focus:ring-cyan-300/60' : 'border-gray-300 bg-white/95 backdrop-blur text-gray-800 hover:opacity-90 focus:ring-indigo-500'}`}
          aria-pressed={darkMode}
          aria-label="Toggle theme"
        >
          {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          {darkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </div>

      {/* Reduce motion support */}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
}

/* -------------------- Data -------------------- */
const TAGS = ["agents", "rag", "evaluations", "governance", "tooling", "open-source"];

const POSTS = [
  { 
    slug: "designing-multi-agent-handshakes", 
    title: "Designing Multi‑Agent Handshakes", 
    date: "2025-05-21", 
    read: "7 min", 
    tags: ["agents", "governance"], 
    excerpt: "Planner/executor handshakes with approvals, budgets, and auditability without killing velocity." 
  },
  { 
    slug: "rag-that-does-not-embarrass-you", 
    title: "RAG that Does Not Embarrass You", 
    date: "2025-04-12", 
    read: "9 min", 
    tags: ["rag", "evaluations"], 
    excerpt: "Freshness policies, query rewriting, rerankers, and evaluation harnesses that actually move the needle." 
  },
  { 
    slug: "tooling-interfaces-for-reliability", 
    title: "Tooling Interfaces for Reliability", 
    date: "2025-03-05", 
    read: "6 min", 
    tags: ["tooling", "agents"], 
    excerpt: "Typed tool schemas, budgets, retries/fallbacks, and safe rollbacks." 
  },
  { 
    slug: "open-source-models-in-production", 
    title: "Open‑Source Models in Production", 
    date: "2025-01-28", 
    read: "8 min", 
    tags: ["open-source", "evaluations"], 
    excerpt: "Running Llama/Mistral with vLLM/TGI, tracing with Langfuse, and tracking eval drift." 
  },
  { 
    slug: "governance-patterns", 
    title: "Governance Patterns for Agentic Systems", 
    date: "2024-12-10", 
    read: "10 min", 
    tags: ["governance", "agents"], 
    excerpt: "SoD, approvals, red‑teaming, and incident response plans stakeholders love." 
  },
  { 
    slug: "observability-for-llm-apps", 
    title: "Observability for LLM Apps", 
    date: "2024-11-17", 
    read: "7 min", 
    tags: ["evaluations", "tooling"], 
    excerpt: "Why traces > logs, attaching prompts/outputs to spans, and telemetry that helps debug quickly." 
  },
];

/* -------------------- Components -------------------- */
function ButtonPrimary({
  children,
  href = "#",
  darkMode,
  small = false,
}) {
  return (
    <a
      href={href}
      className={`inline-flex items-center gap-2 ${
        small ? "px-3 py-1.5 text-sm" : "px-5 py-3"
      } rounded-xl font-medium bg-blue-600 text-white shadow hover:opacity-95 transition focus:outline-none focus-visible:ring-2 ${
        darkMode ? "focus-visible:ring-cyan-300/60" : "focus-visible:ring-indigo-500"
      }`}
    >
      {children}
    </a>
  );
}

function Chip({
  children,
  darkMode,
  active = false,
  onClick,
}) {
  if (active) {
    return (
      <span className={`px-3 py-1.5 rounded-full text-sm font-medium bg-blue-600 text-white`}>
        {children}
      </span>
    );
  }
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full border text-sm transition ${
        darkMode
          ? "border-white/15 bg-white/10 text-white hover:bg-white/15"
          : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}

function PostCard({ p, darkMode }) {
  return (
    <TiltCard
      darkMode={darkMode}
      glow
      className={`${
        darkMode
          ? "bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30"
          : "bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200"
      } rounded-2xl p-5 border`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`${darkMode ? "text-white/60" : "text-gray-600"} text-xs flex items-center gap-2`}>
            <CalendarIcon size={12} /> {new Date(p.date).toLocaleDateString()}
            <span className="opacity-50">•</span>
            <Clock size={12} /> {p.read}
          </div>
          <h3 className="mt-1 font-semibold text-lg">{p.title}</h3>
        </div>
        <div
          className={`${darkMode ? "bg-white/10 text-white" : "bg-indigo-50 text-indigo-700"} p-2 rounded-lg`}
        >
          <FileText size={18} />
        </div>
      </div>
      <p className={`${darkMode ? "text-white/80" : "text-gray-700"} mt-3 text-sm`}>
        {p.excerpt}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs opacity-80">
        {p.tags.map((tag) => (
          <span
            key={tag}
            className={`${
              darkMode ? "bg-white/10 border-white/15" : "bg-gray-50 border-gray-200"
            } rounded-full border px-2 py-1`}
          >
            {tag}
          </span>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Magnetic>
          <ButtonPrimary darkMode={darkMode} href={`/blog/${p.slug}`}>
            Read full post <ArrowRight size={14} />
          </ButtonPrimary>
        </Magnetic>
        <a
          href="#"
          className={`${
            darkMode ? "text-white/80 hover:text-white" : "text-gray-700 hover:text-black"
          } inline-flex items-center gap-1 text-sm`}
        >
          <Github size={14} /> Code examples
        </a>
      </div>
    </TiltCard>
  );
}

// Tilt + Magnetic helpers (same as Projects page)
function TiltCard({
  children,
  className = "",
  glow = false,
  darkMode = false,
}) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const tx = useSpring(rx, { stiffness: 150, damping: 15 });
  const ty = useSpring(ry, { stiffness: 150, damping: 15 });
  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const ROT = 8;
    rx.set((0.5 - py) * ROT);
    ry.set((px - 0.5) * ROT);
    el.style.setProperty("--mx", `${Math.round(px * 100)}%`);
    el.style.setProperty("--my", `${Math.round(py * 100)}%`);
  }
  function onLeave() {
    rx.set(0);
    ry.set(0);
  }
  const glowColor = darkMode
    ? "rgba(34,211,238,0.28)"
    : "rgba(99,102,241,0.25)";
  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: tx, rotateY: ty, transformStyle: "preserve-3d" }}
      className={`group relative will-change-transform transition-shadow hover:-translate-y-0.5 ${className}`}
    >
      {glow && (
        <div
          className="pointer-events-none absolute inset-0 rounded-[inherit] blur-2xl opacity-0 group-hover:opacity-40 transition-opacity"
          style={{
            background: `radial-gradient(520px circle at var(--mx,50%) var(--my,50%), ${glowColor}, transparent 45%)`,
          }}
        />
      )}
      {children}
    </motion.div>
  );
}

/** Magnetic: slight translation toward cursor on hover */
function Magnetic({ children }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 120, damping: 10 });
  const sy = useSpring(y, { stiffness: 120, damping: 10 });

  function onMove(e) {
    const el = ref.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    x.set(((e.clientX - rect.left) / rect.width - 0.5) * 10);
    y.set(((e.clientY - rect.top) / rect.height - 0.5) * 10);
  }
  function onLeave() { x.set(0); y.set(0); }

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy }}
      className="inline-block"
    >
      {children}
    </motion.div>
  );
}

/** Parallax field: same background treatment as Hire Me */
function ParallaxField({ darkMode }) {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {/* Vignette gradient (matches Hire Me) */}
      <div
        className={`absolute inset-0 ${
          darkMode
            ? 'bg-[radial-gradient(120rem_80rem_at_50%_-20%,rgba(79,70,229,0.18),transparent_60%),radial-gradient(80rem_60rem_at_120%_20%,rgba(34,211,238,0.12),transparent_60%)]'
            : 'bg-[radial-gradient(120rem_80rem_at_50%_-20%,rgba(99,102,241,0.12),transparent_60%),radial-gradient(80rem_60rem_at_120%_20%,rgba(34,197,94,0.08),transparent_60%)]'
        }`}
      />

      {/* Subtle grid */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.08]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke={darkMode ? '#ffffff' : '#0f172a'} strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {/* Floating blobs */}
      <div
        className={`absolute left-1/4 top-10 w-40 h-40 rounded-full ${
          darkMode ? 'bg-white/5' : 'bg-indigo-100'
        } blur-3xl animate-[float_10s_ease-in-out_infinite]`}
      />
      <div
        className={`absolute right-10 top-1/3 w-24 h-24 rounded-full ${
          darkMode ? 'bg-indigo-600/20' : 'bg-indigo-200/70'
        } blur-2xl animate-[float_12s_ease-in-out_infinite]`}
      />
      <div
        className={`absolute left-10 bottom-10 w-24 h-24 rounded-full ${
          darkMode ? 'bg-cyan-500/20' : 'bg-cyan-200/70'
        } blur-2xl animate-[float_14s_ease-in-out_infinite]`}
      />

      {/* Diagonal light beam */}
      <div className={`absolute -top-32 -right-40 w-[60rem] h-[20rem] rotate-[20deg] blur-3xl ${darkMode ? 'bg-gradient-to-r from-indigo-500/10 via-cyan-400/10 to-transparent' : 'bg-gradient-to-r from-indigo-400/10 via-cyan-300/10 to-transparent'}`} />

      <style>{`@keyframes float { 0%,100%{ transform: translateY(0)} 50%{ transform: translateY(-12px)} }`}</style>
    </div>
  );
}
